import { Capacitor } from '@capacitor/core';
import { Box, Button, Grid, Stack, Typography, useTheme } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useMap } from 'shared/map-exports';
import { routingFromGeoEvent } from 'shared/routingFromGeoEvent/routingFromGeoEvent';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { useTranslation } from 'react-i18next';
import { HeaderBar, HeaderMenuSection } from 'components/PageLayout';
import { CenterMap } from 'features/mapping/components/CenterMap';
import { RichTextContent } from 'components/RichTextContent';
import { Flooding } from 'features/flooding/flooding.types';
import { useGeoEvent } from 'features/geoEvents/hooks/useGeoEvent/useGeoEvent';
import { Map } from 'features/mapping/components/Map';
import { MapType } from 'features/mapping/mapTypePolicy';
import { ReportMetadata } from 'features/reports/components/ReportMetadata';
import { Report } from 'features/reports/report.types';
import {
  getReportEditPath,
  isRingReport,
} from 'features/reports/reports.utils';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { isEditReportDisabled, usePermissions } from 'hooks/usePermissions';
import { useShareOrCopy } from 'hooks/useShareOrCopy';
import {
  getCurrentLocationAsShareUrl,
  hasValidCoordinates,
} from 'shared/utils';
import { useAuthState } from 'state/useAuthState';

type ReportDetailProps = {
  report: Report;
  displayRelativeDate: boolean;
};

/**
 * Stops in-flight camera movement on the report map when it unmounts. The map
 * is interactive, and because app maps reuse the underlying map instance,
 * leftover pan inertia can carry over to the main map after navigating back.
 * Most flows self-correct because they return to a selected incident/camera
 * whose `CenterMap` re-centers and overrides the stray movement. Opening a
 * report doesn't change the selected incident's coordinates, so nothing
 * re-centers on the way back, and we cancel the movement explicitly here.
 *
 * Rendered inside the report `<Map>` so `useMap().current` is the report map.
 */
const StopReportMapCameraOnUnmount = (): null => {
  const { current: map } = useMap();

  useEffect(() => {
    return () => {
      map?.stop();
    };
  }, [map]);

  return null;
};

const useStyles = makeStyles()((theme) => ({
  textLogo: {
    height: 14,
    width: 138,
    marginTop: 10,
    fill: '#FFFFFF',
  },
  message: {
    whiteSpace: 'pre-line',
    wordBreak: 'break-word',
    paddingRight: theme.spacing(0.5),
  },
  pinchZoomWrapper: {
    width: 'auto',
    height: 'auto',
  },
}));

export const ReportDetail = (props: ReportDetailProps): JSX.Element => {
  const { report, displayRelativeDate } = props;
  const { classes } = useStyles();
  const location = useLocation<{ fromRoute?: string }>();
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    permissions: { canReportWildfire },
  } = useAuthState();

  const media = report?.media?.[0] || {};
  const imgSrc = media.url;

  const { lat, lng } = media;
  const coords = { lat, lng };

  const isFromRing = isRingReport(report);

  const { geoEvent } = useGeoEvent({ id: report.geoEventId }) as {
    geoEvent: Wildfire | Flooding | undefined;
  };

  const { data: permissionsData } = usePermissions({
    geoEventId: geoEvent?.id,
    geoEventType: geoEvent?.geoEventType,
  });
  const editReportDisabled = geoEvent
    ? isEditReportDisabled(geoEvent.geoEventType, permissionsData)
    : false;

  const contentSx = {
    [theme.breakpoints.down('md')]: {
      marginRight: 3,
      marginLeft: 3,
    },
  };

  const getIncidentPath = (): string => {
    const isLegacyIncident = location.pathname.includes('incident');
    return isLegacyIncident
      ? `/incident/${report.incidentId}`
      : `/i/${report.geoEventId}`;
  };

  // When the user arrived from the main map (no incident selected), go back
  // rather than pushing to the incident page.
  const { fromRoute } = location.state ?? {};
  const backAction =
    fromRoute && !routingFromGeoEvent(fromRoute) ? 'back' : getIncidentPath();

  const { canShareOrCopy, shareOrCopyUrl } = useShareOrCopy();

  const editUrl =
    report.showInReportsView && geoEvent
      ? getReportEditPath({
          geoEventType: geoEvent?.geoEventType,
          geoEventId: geoEvent.id,
          reportId: report.id,
        })
      : `/reports/submission/${report.id}`;

  const menuSections: HeaderMenuSection[] =
    canReportWildfire && geoEvent
      ? [
          {
            items: [
              {
                label: t('geoEvent.reports.editPhoto'),
                to: editUrl,
                keyboardShortcut: '',
                disabled: editReportDisabled,
                tooltip: editReportDisabled
                  ? t('geoEvent.reports.editReportDisabledTooltip')
                  : undefined,
              },
            ],
          },
        ]
      : [];

  const handleShare = (): void => {
    shareOrCopyUrl({
      title: report.message,
      text: report.message,
      url: getCurrentLocationAsShareUrl(new Date(report.dateModified)),
      androidDialogTitle: t('geoEvent.reports.shareReport'),
    });
  };

  return (
    <Box
      data-body-scroll-lock-ignore
      sx={{
        minHeight: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        width: '100%',
        paddingBottom: 2,
      }}
    >
      <HeaderBar action={backAction} menuSections={menuSections} />
      <Grid
        container
        sx={{
          flexWrap: 'initial',
          flexDirection: 'column',
          [theme.breakpoints.up('md')]: {
            flexDirection: 'row',
            alignItems: 'stretch',
          },
        }}
      >
        <Grid
          size={{ md: 6, lg: 8 }}
          sx={{
            // Add margin on mobile so a user can scroll the page by swiping on the sides of the screen.
            // For more context see: https://github.com/BetterTyped/react-zoom-pan-pinch/issues/434
            marginLeft: Capacitor.isNativePlatform() ? 2 : 0,
            marginRight: Capacitor.isNativePlatform() ? 2 : 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <TransformWrapper>
            <TransformComponent
              contentClass={classes.pinchZoomWrapper}
              wrapperClass={classes.pinchZoomWrapper}
            >
              <img
                src={imgSrc}
                alt="Media"
                width="100%"
                data-testid="report-image"
                style={{
                  maxHeight: 'calc(100vh - 66px)', // '66px' is the height of the header
                  objectFit: 'contain',
                }}
              />
            </TransformComponent>
          </TransformWrapper>
        </Grid>

        <Grid
          size={{ md: 6, lg: 4 }}
          data-testid="report-content"
          sx={{
            [theme.breakpoints.up('md')]: { pl: 2, pr: 2 },
            [theme.breakpoints.down('md')]: { width: 'calc(100% - 48px)' },
          }}
        >
          <Stack sx={{ pt: 2, height: '100%', width: '100%' }}>
            {/* Attribution with datetime */}
            <Box sx={{ ...contentSx, maxWidth: '100%' }}>
              <ReportMetadata
                displayRelativeDate={displayRelativeDate}
                report={report}
              />
            </Box>

            {/* Message */}
            {report.message && (
              <Box sx={{ ...contentSx, width: '100%' }}>
                <RichTextContent
                  content={report.message}
                  className={classes.message}
                />
              </Box>
            )}

            {/* Share button */}
            {canShareOrCopy && (
              <Button
                aria-label={t('geoEvent.reports.shareReport')}
                color="neutral"
                variant="outlined"
                fullWidth
                onClick={handleShare}
                size="large"
                sx={{ ...contentSx, mb: 3 }}
              >
                {t('geoEvent.reports.shareReport')}
              </Button>
            )}

            {/* Map */}
            {hasValidCoordinates(coords) && (
              <Box
                sx={{
                  ...contentSx,
                  borderRadius: '8px',
                  height: '75vw',
                  maxHeight: 300,
                  overflow: 'hidden',
                  width: '100%',
                }}
              >
                <Map
                  center={{ lat: coords.lat, lng: coords.lng }}
                  zoom={12}
                  mapType={MapType.ReportDetail}
                >
                  {geoEvent?.geoEventType === 'wildfire' && (
                    <Map.WildFireGeoEventMarker geoEvent={geoEvent} />
                  )}
                  {geoEvent?.geoEventType === 'flooding' && (
                    <Map.FloodingGeoEventMarker geoEvent={geoEvent} />
                  )}
                  <Map.ReportMediaMarkers
                    reports={report}
                    isFadable={false}
                    interactive={false}
                    showObfuscation={isFromRing}
                  />
                  <CenterMap
                    latLng={{ lat: coords.lat, lng: coords.lng }}
                    defaultSnapPointPct={0}
                  />
                  <StopReportMapCameraOnUnmount />
                </Map>
              </Box>
            )}

            {/* Ring attribution */}
            {isFromRing && (
              <Box sx={{ mt: 'auto', textAlign: 'center' }}>
                <Stack gap={2} mt={2} sx={{ width: '100%' }}>
                  <Box>
                    <Typography
                      variant="subtitle1"
                      color="secondary"
                      sx={{
                        textAlign: 'center',
                        textTransform: 'uppercase',
                        fontWeight: 'bold',
                      }}
                    >
                      {t('geoEvent.reports.attribution.header')}
                    </Typography>
                  </Box>
                  <Box>
                    <img
                      src="/images/ring-logo-white.svg"
                      alt={t('geoEvent.reports.attribution.ring')}
                      style={{ maxWidth: '64px' }}
                    />
                  </Box>
                </Stack>
              </Box>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};
